import {
  Badge,
  BadgeVariant,
  Box,
  Button,
  Card,
  Icon,
  Pagination,
  Table,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useSearchParams } from 'react-router-dom';

import {
  AppliedDataFilter,
  IncentiveListItem,
  IncentiveStatus,
  IncentiveStatusLabels,
  IncentiveTypeLabels,
  ProgramTypeLabels,
  UserPermissions,
} from 'types';

import { useGetIncentiveFiltersQuery, useLazyGetIncentivesQuery } from '../../services/incentives';
import RequirePermissions from '../auth/requirePermissions';
import { FilterBar } from '../filters';
import { AddFilterButtonDropdown } from '../filters/AddFilterButton';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';

import { IncentiveModal } from './IncentiveModal';

const PAGE_SIZE = 20;

function getBadgeVariantForIncentiveStatus(status: IncentiveStatus): BadgeVariant {
  switch (status) {
    case IncentiveStatus.active:
      return 'success';
    case IncentiveStatus.inactive:
    default:
      return 'default';
  }
}

export const Incentives = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [trigger, { data: searchResponse, isFetching, isError }] = useLazyGetIncentivesQuery();
  const [appliedFilters, setAppliedFilters] = useState<AppliedDataFilter[]>([]);
  const [selectedIncentive, setSelectedIncentive] = useState<IncentiveListItem | null>(null);
  const { data: filterOptions = [], isLoading: isFiltersLoading } = useGetIncentiveFiltersQuery();

  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();

  const { incentives = [], total = 0 } = searchResponse || {};

  const currentPage = searchParams.get('page');
  const advancedFilters = searchParams.get('advancedFilters');
  const searchTerm = searchParams.get('search') || undefined;

  useEffect(() => {
    const pageNum = currentPage ? Number(currentPage) : 1;
    const filters = advancedFilters || JSON.stringify([]);

    setAppliedFilters(JSON.parse(filters));
    trigger({
      pageNum,
      pageSize: PAGE_SIZE,
      advancedFilters: filters,
      searchTerm,
    });
  }, [trigger, currentPage, advancedFilters, searchTerm]);

  const handleApplyAdvancedFilters = useCallback(
    (filtersToApply: AppliedDataFilter[]) => {
      if (filtersToApply.some((filter) => filter.selectedValues && filter.selectedValues.length)) {
        searchParams.set('advancedFilters', JSON.stringify(filtersToApply));
        setSearchParams(searchParams);
      }
      searchParams.delete('page');
      setAppliedFilters(filtersToApply);
    },
    [setAppliedFilters, searchParams, setSearchParams],
  );

  const handleRemoveAdvancedFilter = useCallback(
    (filterId: string) => {
      const newFilters = appliedFilters.filter((filter) => filter.id !== filterId);
      if (newFilters.length) {
        searchParams.set('advancedFilters', JSON.stringify(newFilters));
      } else {
        searchParams.delete('advancedFilters');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
      setAppliedFilters(newFilters);
    },
    [appliedFilters, setAppliedFilters, searchParams, setSearchParams],
  );

  const handleClearAdvancedFilters = useCallback(() => {
    searchParams.delete('advancedFilters');
    searchParams.delete('page');
    setSearchParams(searchParams);
    setAppliedFilters([]);
  }, [setAppliedFilters, searchParams, setSearchParams]);

  const onPageChange = (newPage: number) => {
    searchParams.set('page', newPage.toString());
    setSearchParams(searchParams);
  };

  const onSearchChange = (searchTerm: string) => {
    if (searchTerm && searchTerm !== '') {
      searchParams.set('search', searchTerm);
    } else {
      searchParams.delete('search');
    }
    searchParams.delete('page');
    setSearchParams(searchParams);
  };

  const handleCreateClick = () => {
    setSelectedIncentive(null);
    openModal();
  };

  const handleEditClick = (incentive: IncentiveListItem) => {
    setSelectedIncentive(incentive);
    openModal();
  };

  const handleModalClose = () => {
    setSelectedIncentive(null);
    closeModal();
  };

  const columns = [
    {
      heading: 'Name',
      dataKey: 'name',
      render: (_cell: any, row: any) => <Link to={`/admin/incentives/${row.key}`}>{row.name}</Link>,
    },
    {
      heading: 'Type',
      dataKey: 'type',
      width: 100,
      render: (_cell: any, row: any) => IncentiveTypeLabels[row.type as keyof typeof IncentiveTypeLabels],
    },
    {
      heading: 'Allocations',
      dataKey: 'allocationCount',
      width: 100,
    },
    {
      heading: 'Program Types',
      dataKey: 'programTypes',
      width: 150,
      render: (_cell: any, row: any) =>
        row.programTypes?.length
          ? row.programTypes.map((pt: string) => ProgramTypeLabels[pt as keyof typeof ProgramTypeLabels]).join(', ')
          : 'Global',
    },
    {
      heading: 'Status',
      dataKey: 'status',
      width: 100,
      render: (_cell: any, row: any) => (
        <Badge
          variant={getBadgeVariantForIncentiveStatus(row.status)}
          message={IncentiveStatusLabels[row.status as IncentiveStatus]}
        />
      ),
    },
    {
      heading: 'Actions',
      dataKey: 'actions',
      width: 80,
      render: (_cell: any, row: any) => (
        <RequirePermissions permissions={[UserPermissions.lightReachIncentiveAdmin]}>
          <Button
            iconPrefix="pencil"
            size="sm"
            variant="secondary"
            aria-label="Edit Incentive"
            onClick={() => handleEditClick(row as IncentiveListItem)}
          />
        </RequirePermissions>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Incentives</title>
      </Helmet>
      <MainContainer>
        <PageHeader title="Incentives">
          <RequirePermissions permissions={[UserPermissions.lightReachIncentiveAdmin]}>
            <Button variant="primary" size="md" onClick={handleCreateClick}>
              + New Incentive
            </Button>
          </RequirePermissions>
        </PageHeader>
        <Card padding="sm">
          <Box margin="0 lg 0 lg">
            <FilterBar
              filtersState={{ searchTerm }}
              handleSearchTerm={(searchTerm: string) => onSearchChange(searchTerm)}
              placeholder="Search by name, key, or keyword..."
            >
              {!isFiltersLoading && (
                <AddFilterButtonDropdown
                  dataFilters={filterOptions}
                  appliedFilters={appliedFilters}
                  onRemoveFilter={handleRemoveAdvancedFilter}
                  onApplyFilters={handleApplyAdvancedFilters}
                  onClearFilters={handleClearAdvancedFilters}
                />
              )}
            </FilterBar>
            {isError ? (
              <Box padding="xl" textAlign="center">
                <Box direction="row" justifyContent="center" alignItems="center" childGap="sm">
                  <Icon name="t-warning" color="danger-500" />
                  There was a problem retrieving incentives
                </Box>
              </Box>
            ) : (
              <Table
                isLoading={isFetching}
                rowKey="key"
                columns={columns}
                rows={incentives}
                isScrollable={{ x: true }}
              />
            )}
          </Box>
          {incentives.length > 0 && (
            <Pagination
              activePage={Number(currentPage) || 1}
              arePagesVisible
              itemsPerPage={PAGE_SIZE}
              numberOfPagesDisplayed={3}
              onChange={(page) => onPageChange(page)}
              totalItemsCount={total}
              isCompact
            />
          )}
        </Card>
      </MainContainer>
      <IncentiveModal isOpen={isModalOpen} onClose={handleModalClose} incentive={selectedIncentive} />
    </>
  );
};
